import React, { useState } from 'react';
import { ActivityIndicator, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';

import { Modal } from '@/components/ui/modal';
import { useAccounts, useInvalidateAccounting } from '@/hooks/use-accounting';
import { accountingService } from '@/services/accounting-service';
import { ImportBatchKind, type Account } from '@/types/models';
import { pickAndParseFiles } from '@/utils/accounting-export';
import { downloadCsvTemplate } from '@/utils/csv-templates';
import { formatParPercent } from '@/utils/par-value';
import { extractParValueRows } from '@/utils/par-value-sheet';

interface Props {
	associationId: string;
	onClose: () => void;
	/** Called after a successful import so the caller can refetch the roster. */
	onDone?: () => void;
}

/** A row that resolved to a real account and is ready to write. */
interface MatchedRow {
	accountId: string;
	accountNumber: string;
	label: string;
	parValueBps: number;
}

type Phase = 'idle' | 'reading' | 'preview' | 'importing' | 'done';

/**
 * Import ownership percentages onto EXISTING accounts.
 *
 * Deliberately narrow: it writes `par_value_bps` through `/accounts/bulk-par-value`
 * and nothing else. The customer-balances importer can also carry pct_ownership,
 * but it overwrites address / external_name on every row it touches — so
 * re-uploading a roster purely to correct shares would stomp hand-edited
 * addresses. This path exists so correcting a percentage is not a roster import.
 *
 * A row whose account_number matches nothing is REPORTED AND SKIPPED, never
 * created. That keeps a wrong-format file (e.g. `4491` when the accounts are
 * `4491CH`) a visible no-op instead of 30 duplicate accounts.
 */
export const ImportParValuesModal: React.FC<Props> = ({ associationId, onClose, onDone }) => {
	const { data: accountsResp, isLoading: accountsLoading } = useAccounts({ associationId, includeArchived: true, limit: 2000 });
	const invalidate = useInvalidateAccounting();

	const [phase, setPhase] = useState<Phase>('idle');
	const [matched, setMatched] = useState<MatchedRow[]>([]);
	const [unmatched, setUnmatched] = useState<Array<{ accountNumber: string; reason: string }>>([]);
	const [fileErrors, setFileErrors] = useState<string[]>([]);
	const [readNotes, setReadNotes] = useState<string[]>([]);
	const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
	const [result, setResult] = useState<{ updated: number; failed: number } | null>(null);

	const pick = async () => {
		setFileErrors([]);
		setResult(null);
		setPhase('reading');
		try {
			const files = await pickAndParseFiles();
			if (files.length === 0) { setPhase('idle'); return; }

			const accounts = (accountsResp?.data ?? []) as Account[];
			const byNumber = new Map(
				accounts.filter((a) => a.accountNumber).map((a) => [a.accountNumber!.trim().toLowerCase(), a]),
			);

			const nextMatched: MatchedRow[] = [];
			const nextUnmatched: Array<{ accountNumber: string; reason: string }> = [];
			const errs: string[] = [];
			const notes: string[] = [];
			// One account can only carry one share; last row wins, but say so.
			const seen = new Set<string>();

			for (const f of files) {
				const sheet = extractParValueRows(f.cells, byNumber.keys());
				if (sheet.error) {
					errs.push(`${f.name}: ${sheet.error}`);
					continue;
				}
				notes.push(
					`${f.name}: account # from “${sheet.accountColumn ?? 'account'}”, ownership from “${sheet.percentColumn}”`
					+ (sheet.readAsFractions ? ' (read as fractions of 1 — 0.01366 = 1.366%)' : ''),
				);
				nextUnmatched.push(...sheet.skipped);
				for (const row of sheet.rows) {
					const account = byNumber.get(row.accountNumber.toLowerCase());
					if (!account) continue;
					if (seen.has(account.id)) {
						nextUnmatched.push({ accountNumber: row.accountNumber, reason: 'duplicate row for this account — later value used' });
					}
					seen.add(account.id);
					nextMatched.push({
						accountId: account.id,
						accountNumber: row.accountNumber,
						label: account.primaryOwnerName?.trim() || account.address?.trim() || row.accountNumber,
						parValueBps: row.parValueBps,
					});
				}
			}

			setMatched(nextMatched);
			setUnmatched(nextUnmatched);
			setReadNotes(notes);
			setFileErrors(errs);
			setPhase('preview');
		} catch (e) {
			setFileErrors([(e as Error)?.message || 'Failed to read the file.']);
			setPhase('idle');
		}
	};

	const runImport = async () => {
		if (matched.length === 0) return;
		setPhase('importing');
		const errors: string[] = [];
		let updated = 0;

		let batchId: string | undefined;
		try {
			const batch = await accountingService.createImportBatch({
				kind: ImportBatchKind.ParValues,
				associationId,
				sourceFilename: 'Ownership percentages CSV',
			});
			batchId = batch.id;
		} catch {
			// Non-fatal: the write itself is idempotent, only the audit trail is lost.
		}

		// Client loops short chunked POSTs — the mandatory bulk pattern. Setting a
		// share to the value it already holds is a no-op, so a chunk that fails
		// halfway is safe to re-drive.
		const CHUNK = 200;
		for (let start = 0; start < matched.length; start += CHUNK) {
			const slice = matched.slice(start, start + CHUNK);
			setProgress({ done: start, total: matched.length });
			try {
				const r = await accountingService.bulkSetParValue({
					associationId,
					importBatchId: batchId,
					items: slice.map((m) => ({ accountId: m.accountId, parValueBps: m.parValueBps })),
				});
				updated += r.updated;
				for (const e of r.errors) errors.push(`${e.accountId}: ${e.message}`);
			} catch (e) {
				errors.push(`Chunk failed (safe to re-run): ${(e as Error).message}`);
				break;
			}
		}
		setProgress({ done: matched.length, total: matched.length });
		await invalidate();
		setFileErrors(errors);
		setResult({ updated, failed: matched.length - updated });
		setPhase('done');
		onDone?.();
	};

	const totalBps = matched.reduce((s, m) => s + m.parValueBps, 0);
	const offBy = totalBps - 10_000;
	// Par value is a declared legal share, so the set must reconcile to 100%.
	// Off by more than a rounding basis point is a data problem worth surfacing
	// BEFORE it becomes 30 wrong assessments.
	const reconciles = Math.abs(offBy) <= 1;

	return (
		<Modal visible onDismiss={onClose} contentContainerStyle={styles.modal}>
			<View style={styles.header}>
				<Text style={styles.title}>Import ownership percentages</Text>
				<Pressable onPress={onClose} hitSlop={10}><Text style={styles.close}>✕</Text></Pressable>
			</View>
			<ScrollView contentContainerStyle={styles.body}>
				<Text style={styles.hint}>
					A CSV or Excel file with each customer&apos;s account # in one column and their ownership percent in
					another — headed <Text style={styles.code}>Par Value</Text>, <Text style={styles.code}>Ownership %</Text> or{' '}
					<Text style={styles.code}>pct_ownership</Text>. The association&apos;s own fee sheet works as-is. Sets each
					existing customer&apos;s share of the annual budget. Nothing else on the account is changed, and no balances
					are posted.
				</Text>

				{(phase === 'idle' || phase === 'reading') && (
					<>
						<Pressable
							style={[styles.primary, (phase === 'reading' || accountsLoading) && { opacity: 0.5 }]}
							disabled={phase === 'reading' || accountsLoading}
							onPress={pick}
						>
							<Text style={styles.primaryLabel}>
								{accountsLoading ? 'Loading accounts…' : phase === 'reading' ? 'Reading…' : 'Pick file…'}
							</Text>
						</Pressable>
						<Pressable style={styles.templateRow} onPress={() => downloadCsvTemplate('par-values')}>
							<Text style={styles.templateLabel}>⭳ Download the template</Text>
						</Pressable>
					</>
				)}

				{phase === 'preview' && (
					<>
						<View style={styles.statRow}>
							<Text style={styles.statOk}>✓ {matched.length} matched</Text>
							{unmatched.length > 0 && <Text style={styles.statWarn}>⚠ {unmatched.length} skipped</Text>}
						</View>
						{readNotes.map((n) => <Text key={n} style={styles.readNote}>{n}</Text>)}

						{matched.length > 0 && (
							<View style={[styles.reconBox, !reconciles && styles.reconBoxBad]}>
								<Text style={[styles.reconValue, !reconciles && styles.bad]}>
									Shares total {formatParPercent(totalBps)}
								</Text>
								<Text style={[styles.reconNote, !reconciles && styles.bad]}>
									{reconciles
										? 'reconciles to 100%'
										: `${offBy > 0 ? 'over' : 'under'} 100% by ${Math.abs(offBy / 100).toFixed(2)}% — check the file before importing`}
								</Text>
							</View>
						)}

						{matched.length > 0 && (
							<View style={styles.previewBox}>
								{matched.slice(0, 6).map((m) => (
									<Text key={m.accountId} style={styles.previewRow} numberOfLines={1}>
										{m.accountNumber} · {m.label} · {formatParPercent(m.parValueBps)}
									</Text>
								))}
								{matched.length > 6 && <Text style={styles.previewMore}>+{matched.length - 6} more</Text>}
							</View>
						)}

						{unmatched.length > 0 && (
							<View style={styles.errorsBox}>
								<Text style={styles.errorTitle}>Skipped — no account was created or changed</Text>
								{unmatched.slice(0, 10).map((u, i) => (
									<Text key={i} style={styles.errorLine}>{u.accountNumber} — {u.reason}</Text>
								))}
								{unmatched.length > 10 && <Text style={styles.errorLine}>…and {unmatched.length - 10} more</Text>}
								{matched.length === 0 && (
									<Text style={styles.errorHint}>
										Nothing matched. The account numbers in the file don&apos;t look like the ones in this
										association — check whether they need a prefix or suffix.
									</Text>
								)}
							</View>
						)}

						<View style={{ flexDirection: 'row', gap: 8 }}>
							<Pressable style={styles.secondary} onPress={() => { setPhase('idle'); setMatched([]); setUnmatched([]); }}>
								<Text style={styles.secondaryLabel}>Back</Text>
							</Pressable>
							<Pressable
								style={[styles.primary, { flex: 1 }, matched.length === 0 && { opacity: 0.4 }]}
								disabled={matched.length === 0}
								onPress={runImport}
							>
								<Text style={styles.primaryLabel}>Set {matched.length} ownership %</Text>
							</Pressable>
						</View>
					</>
				)}

				{phase === 'importing' && (
					<View style={styles.progressBox}>
						<ActivityIndicator />
						<Text style={styles.progressLabel}>
							Setting ownership percentages {progress?.done ?? 0} / {progress?.total ?? matched.length}…
						</Text>
					</View>
				)}

				{phase === 'done' && result && (
					<>
						<View style={styles.resultBox}>
							<Text style={styles.resultOk}>✓ {result.updated} ownership percentage{result.updated === 1 ? '' : 's'} set</Text>
							{result.failed > 0 && <Text style={styles.resultFail}>✗ {result.failed} failed</Text>}
						</View>
						<Pressable style={styles.primary} onPress={onClose}>
							<Text style={styles.primaryLabel}>Done</Text>
						</Pressable>
					</>
				)}

				{fileErrors.length > 0 && (
					<View style={styles.errorsBox}>
						{fileErrors.slice(0, 10).map((e, i) => (
							<Text key={i} style={styles.errorLine}>{e}</Text>
						))}
					</View>
				)}
			</ScrollView>
		</Modal>
	);
};

const styles = StyleSheet.create({
	modal: { width: '92%', maxWidth: 560, maxHeight: '88%', borderRadius: 12, padding: 0 },
	header: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: 16, borderBottomWidth: 1, borderBottomColor: '#eee' },
	title: { fontSize: 18, fontWeight: '700' },
	close: { fontSize: 20, color: '#888', paddingHorizontal: 8 },
	body: { padding: 20, gap: 12 },
	hint: { color: '#546e7a', fontSize: 13, lineHeight: 19 },
	code: { fontFamily: 'monospace', backgroundColor: '#eceff1', paddingHorizontal: 4, borderRadius: 3 },
	primary: { backgroundColor: '#00695c', padding: 12, borderRadius: 6, alignItems: 'center' },
	primaryLabel: { color: '#fff', fontWeight: '600' },
	secondary: { backgroundColor: '#eceff1', padding: 12, borderRadius: 6, alignItems: 'center', paddingHorizontal: 16 },
	secondaryLabel: { color: '#37474f', fontWeight: '500' },
	templateRow: { paddingVertical: 6, alignItems: 'center' },
	templateLabel: { color: '#1976d2', fontWeight: '600', fontSize: 13 },
	statRow: { flexDirection: 'row', gap: 16, alignItems: 'baseline' },
	statOk: { color: '#2e7d32', fontWeight: '700' },
	statWarn: { color: '#ef6c00', fontWeight: '700' },
	readNote: { fontSize: 12, color: '#546e7a' },
	reconBox: { padding: 10, backgroundColor: '#e8f5e9', borderRadius: 6 },
	reconBoxBad: { backgroundColor: '#ffebee' },
	reconValue: { fontWeight: '700', color: '#2e7d32' },
	reconNote: { fontSize: 12, color: '#558b2f' },
	bad: { color: '#c62828' },
	previewBox: { padding: 10, backgroundColor: '#f5f7fa', borderRadius: 6 },
	previewRow: { fontSize: 12, color: '#455a64', fontFamily: 'monospace', paddingVertical: 2 },
	previewMore: { fontSize: 12, color: '#78909c', fontStyle: 'italic', marginTop: 4 },
	errorsBox: { padding: 10, backgroundColor: '#fff3e0', borderRadius: 6, borderLeftWidth: 3, borderLeftColor: '#fbc02d', gap: 2 },
	errorTitle: { color: '#e65100', fontWeight: '700', fontSize: 12, marginBottom: 2 },
	errorLine: { color: '#795548', fontSize: 12 },
	errorHint: { color: '#bf360c', fontSize: 12, fontStyle: 'italic', marginTop: 6, lineHeight: 17 },
	progressBox: { padding: 16, backgroundColor: '#e3f2fd', borderRadius: 6, gap: 8, alignItems: 'center' },
	progressLabel: { color: '#0d47a1', fontWeight: '600', fontSize: 13 },
	resultBox: { padding: 12, backgroundColor: '#e8f5e9', borderRadius: 6, gap: 4 },
	resultOk: { color: '#2e7d32', fontWeight: '600' },
	resultFail: { color: '#c62828', fontWeight: '600' },
});
